import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@/model';
import { CostFactorInsightCard } from './CostFactorInsightCard';
import { SlideOutSheet, EditIconButton } from '@/components';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import EditHealthCareInflationModal from '@/components/EditHealthCareInflationModal';
import { HealthcareInflationCitation } from '@/components/HealthcareInflationCitation';
import { CostDataMethodologySlideOutContent } from '@/components/LearnMoreSlideOutContent';

import {
  CalendarFoldIcon,
  CalendarHeartIcon,
  ChartNoAxesCombinedIcon,
  CheckCircleIcon,
  CircleDollarSignIcon,
  HeartHandshakeIcon,
  LandmarkIcon,
  MapPinHouseIcon,
  Settings2Icon,
  TagIcon,
  MapPin,
  TrendingUp,
  Heart,
  CircleQuestionMark,
} from 'lucide-react';

import {
  formatPercent,
  formatThousands,
  CareEnvironment,
  isDurationCustom,
  areNumbersClose,
  defaultHealthcareInflation,
  isNullOrUndefined,
  appliedHealthCareInflation,
} from '@shared';

export function CostFactorInsightCards() {
  const client = useSelector(selectClient)!;
  const {
    allPhaseCosts: {
      allPhaseTotalCost,
      allPhaseInflatedFamilyCareCost,
      allPhaseInflatedProfessionalShareCost,
      allPhaseCareMonthsNeeded,
      allPhaseFamilyProvidedPercent,
      allPhaseFamilyCareHoursProvided,
    },
    intakeSurvey: {
      clientIndependencePreference,
      clientFinancialSecurityPreference,
      clientFamilyInvolvementPreference,
      clientHasStartedLtc,
    },
    clientFirstName,
    clientPhasePredictedStartYears,
    phaseCosts,
    clientCalculationSettings: { healthCareInflationRate: clientHealthCareInflationRate },
    clientLocalCareEnvironmentCosts,
    clientInsights,
  } = client;

  const allPhaseInflatedCost =
    allPhaseInflatedProfessionalShareCost + allPhaseInflatedFamilyCareCost;
  const healthCareInflationContribution = allPhaseInflatedCost - allPhaseTotalCost;

  const defaultInflationRate = defaultHealthcareInflation(client);
  const appliedInflationRate = appliedHealthCareInflation(client);
  const isInflationEdited =
    !isNullOrUndefined(clientHealthCareInflationRate) &&
    !areNumbersClose(clientHealthCareInflationRate, defaultInflationRate);

  const rateLocaleType = clientLocalCareEnvironmentCosts[CareEnvironment.home].rateLocaleType;
  const appliedZipCode = clientLocalCareEnvironmentCosts[CareEnvironment.home].zipCode;

  const careDurationEdited = isDurationCustom(phaseCosts);

  const [editModalOpen, setEditModalOpen] = useState(false);

  const validStartYears = Object.values(clientPhasePredictedStartYears).filter(
    (y): y is number => typeof y === 'number' && y > 0,
  );
  const projectedStartYear = validStartYears.length > 0 ? Math.min(...validStartYears) : 0;
  const projectedEndYear =
    projectedStartYear > 0 ? projectedStartYear + Number(allPhaseCareMonthsNeeded ?? 0) / 12 : 0;

  const costOfLivingInsight = clientInsights?.find(i => i.id === 'cost-of-living');
  const inflationInsight = clientInsights?.find(i => i.id === 'inflation-horizon');
  const familyInsight = clientInsights?.find(i => i.id === 'family-caregiving');

  return (
    <>
      <div className="flex flex-col gap-4">
        {costOfLivingInsight && (
          <CostFactorInsightCard
            insight={costOfLivingInsight}
            icon={<MapPin className="text-primary size-5" />}
            cost={allPhaseTotalCost}
            changeType="increase"
            expandedContent={
              <CostOfCareDetail
                clientFirstName={clientFirstName}
                rateLocaleType={rateLocaleType}
                appliedZipCode={appliedZipCode}
                ltcDurationYears={allPhaseCareMonthsNeeded / 12}
                clientIndependencePreference={clientIndependencePreference}
                clientFinancialSecurityPreference={clientFinancialSecurityPreference}
                clientHasStartedLtc={clientHasStartedLtc}
                careDurationEdited={careDurationEdited}
              />
            }
          />
        )}
        {inflationInsight && (
          <CostFactorInsightCard
            insight={inflationInsight}
            icon={<TrendingUp className="text-primary size-5" />}
            cost={healthCareInflationContribution}
            changeType="increase"
            expandedContent={
              <HealthcareInflationDetail
                client={client}
                currentCost={allPhaseTotalCost}
                projectedStartYear={Math.round(projectedStartYear)}
                projectedEndYear={Math.round(projectedEndYear)}
                appliedInflationRate={appliedInflationRate}
                isInflationEdited={isInflationEdited}
                healthCareInflationContribution={healthCareInflationContribution}
                setEditModalOpen={setEditModalOpen}
              />
            }
          />
        )}
        {familyInsight && (
          <CostFactorInsightCard
            insight={familyInsight}
            icon={<Heart className="text-primary size-5" />}
            cost={allPhaseInflatedFamilyCareCost}
            changeType="decrease"
            expandedContent={
              <FamilyCaregivingDetail
                clientFirstName={clientFirstName}
                clientFamilyInvolvementPreference={clientFamilyInvolvementPreference}
                allPhaseFamilyProvidedPercent={allPhaseFamilyProvidedPercent}
                allPhaseFamilyCareHoursProvided={allPhaseFamilyCareHoursProvided}
                allPhaseInflatedFamilyCareCost={allPhaseInflatedFamilyCareCost}
                rateLocaleType={rateLocaleType}
                appliedZipCode={appliedZipCode}
              />
            }
          />
        )}
      </div>
      <EditHealthCareInflationModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
      />
    </>
  );
}

function localCostString(rateLocaleType: RateLocaleType, zipCode: string) {
  switch (rateLocaleType) {
    case 'National':
      return 'The US';
    case 'State':
      return 'Your State';
    case 'Local':
      return `Zip Code ${zipCode}`;
  }
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-muted grid size-10 shrink-0 place-items-center rounded-lg text-gray-950 md:size-11">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-foreground text-sm">{label}</span>
        <span className="text-sm font-medium text-gray-950 md:text-base">{value}</span>
      </div>
    </div>
  );
}

function PreferencesRow({ icon, label, values }: { icon: React.ReactNode; label: string; values: string[] }) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-muted grid size-10 shrink-0 place-items-center rounded-lg text-gray-950 md:size-11">
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-foreground text-sm">{label}</span>
        <div className="mt-2 flex flex-col gap-2">
          {values.map((el, i) => (
            <span key={i} className="flex items-baseline gap-1.5 text-sm text-gray-950">
              <CheckCircleIcon className="size-3 shrink-0 translate-y-px" />
              {el}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

interface CostOfCareDetailProps {
  clientFirstName: string;
  rateLocaleType: RateLocaleType;
  appliedZipCode: string;
  ltcDurationYears: number;
  clientIndependencePreference: string;
  clientFinancialSecurityPreference: string;
  clientHasStartedLtc: boolean;
  careDurationEdited: boolean;
}

function CostOfCareDetail({
  clientFirstName,
  rateLocaleType,
  appliedZipCode,
  ltcDurationYears,
  clientIndependencePreference,
  clientFinancialSecurityPreference,
  clientHasStartedLtc,
  careDurationEdited,
}: CostOfCareDetailProps) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<CalendarFoldIcon className="size-4 md:size-5" />}
        label={`${careDurationEdited ? 'Edited' : 'Projected'} care timeline`}
        value={`${formatThousands(ltcDurationYears, '', 1)} years ${clientHasStartedLtc ? 'remaining' : ''}`}
      />
      <DetailRow
        icon={<MapPinHouseIcon className="size-4 md:size-5" />}
        label="Market rates for care in"
        value={
          <span className="flex items-center gap-1">
            {localCostString(rateLocaleType, appliedZipCode)}
            <SlideOutSheet
              slideOutContent={<CostDataMethodologySlideOutContent />}
              title="Cost Data"
            >
              <CircleQuestionMark className="text-foreground hover:text-foreground size-3 -translate-y-0.5 hover:cursor-pointer" />
            </SlideOutSheet>
          </span>
        }
      />
      {(clientIndependencePreference || clientFinancialSecurityPreference) && (
        <PreferencesRow
          icon={<Settings2Icon className="size-4 md:size-5" />}
          label={`${clientFirstName}'s preferences:`}
          values={[clientIndependencePreference, clientFinancialSecurityPreference].filter(Boolean)}
        />
      )}
    </div>
  );
}

interface HealthcareInflationDetailProps {
  client: Client;
  currentCost: number;
  projectedStartYear: number;
  projectedEndYear: number;
  appliedInflationRate: number;
  isInflationEdited: boolean;
  healthCareInflationContribution: number;
  setEditModalOpen: (open: boolean) => void;
}

function HealthcareInflationDetail({
  client,
  currentCost,
  projectedStartYear,
  projectedEndYear,
  appliedInflationRate,
  isInflationEdited,
  healthCareInflationContribution,
  setEditModalOpen,
}: HealthcareInflationDetailProps) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<TagIcon className="size-4 md:size-5" />}
        label="Current cost:"
        value={<LtcCostDisplay value={currentCost} />}
      />
      <DetailRow
        icon={<CalendarHeartIcon className="size-4 md:size-5" />}
        label="Projected care start to end year:"
        value={projectedStartYear > 0 ? `${projectedStartYear} - ${projectedEndYear}` : '-'}
      />
      <DetailRow
        icon={<LandmarkIcon className="size-4 md:size-5" />}
        label={`${isInflationEdited ? 'Edited healthcare inflation¹:' : 'Healthcare inflation¹:'}`}
        value={
          <span className="flex items-center">
            <span>{`${formatPercent(appliedInflationRate, 1)} annually`}</span>
            <EditIconButton
              onClick={() => setEditModalOpen(true)}
              tooltipText="Edit healthcare inflation rate"
            />
          </span>
        }
      />
      <DetailRow
        icon={<ChartNoAxesCombinedIcon className="size-4 md:size-5" />}
        label="Future projected cost²:"
        value={
          <LtcCostDisplay value={currentCost + healthCareInflationContribution} />
        }
      />
      <div className="text-muted-foreground mt-2 space-y-1 text-xs leading-snug">
        <p>2. <HealthcareInflationCitation client={client} /></p>
        <p>3. This simplified explanation is for educational purposes only and may slightly overestimate the actual calculation used to estimate cost.</p>
      </div>
    </div>
  );
}

interface FamilyCaregivingDetailProps {
  clientFirstName: string;
  clientFamilyInvolvementPreference: string;
  allPhaseFamilyProvidedPercent: number;
  allPhaseFamilyCareHoursProvided: number;
  allPhaseInflatedFamilyCareCost: number;
  rateLocaleType: RateLocaleType;
  appliedZipCode: string;
}

function FamilyCaregivingDetail({
  clientFirstName,
  clientFamilyInvolvementPreference,
  allPhaseFamilyProvidedPercent,
  allPhaseFamilyCareHoursProvided,
  allPhaseInflatedFamilyCareCost,
  rateLocaleType,
  appliedZipCode,
}: FamilyCaregivingDetailProps) {
  return (
    <div className="space-y-4">
      <DetailRow
        icon={<HeartHandshakeIcon className="size-4 md:size-5" />}
        label="Projected family caregiving:"
        value={`${formatThousands(allPhaseFamilyCareHoursProvided, '', 0)} hours (${formatPercent(allPhaseFamilyProvidedPercent, 1)} of care needs)`}
      />
      <DetailRow
        icon={<CircleDollarSignIcon className="size-4 md:size-5" />}
        label="Projected value of care provided by family:"
        value={<LtcCostDisplay value={allPhaseInflatedFamilyCareCost} />}
      />
      <DetailRow
        icon={<MapPinHouseIcon className="size-4 md:size-5" />}
        label="Based on market rates for home care in"
        value={localCostString(rateLocaleType, appliedZipCode)}
      />
      {clientFamilyInvolvementPreference && (
        <PreferencesRow
          icon={<Settings2Icon className="size-4 md:size-5" />}
          label={`${clientFirstName}'s preferences:`}
          values={[clientFamilyInvolvementPreference]}
        />
      )}
    </div>
  );
}
